import CTNButton from '@ctn/ui/components/ctn-button';
import CTNCard from '@ctn/ui/components/ctn-card';
import CTNCardOverlayTemplate from '@ctn/ui/components/ctn-card/templates/CTNCardOverlayTemplate';
import CTNCarousel from '@ctn/ui/components/ctn-carousel';
import { CTNIcon } from '@ctn/ui/components/ctn-icon';
import { useNotification } from '@ctn/ui/components/ctn-notifications';
import { useEffect } from 'react';
import { type Location, useLocation } from 'react-router-dom';

import { CardIconTemplate } from '@/components/CardsTemplate';
import { ImageMedia } from '@/components/ImageMedia';
import Footer from '@/containers/Footer';
import { getKeyCloakLogin } from '@/services/login.service';

import { ROUTES_ICON, ROUTES_LABEL, ROUTES_RESOURCES } from '@/constants/routes.constants';
import { BREAKPOINTS } from '@/constants/viewport.constants';
import { notificationsConfig } from '@/helpers/notifications';
import type { Icon } from '@ctn/ui/types/generic';
import type { Settings } from 'react-slick';

import Dispositivos from '@/assets/images/home-01.webp';
import BackgroundImage from '@/assets/images/home-bg.webp';

const CONTENT = {
	DIVISION_HORIZONTAL:
		'Envía solicitudes para la tramitación digital de divisiones horizontales y su gestión notarial.',
	COMPRAVENTA:
		'Envía solicitudes de compraventa y gestiona su tramitación notarial de forma digital.',
	PODERES:
		'Envía solicitudes de poderes y gestiona su tramitación notarial de forma digital.',
	ACTA_FIN_OBRA:
		'Envía solicitudes de acta final de obra y gestiona su tramitación notarial de forma digital.',
	DON: 'Envío solicitudes para obra nueva terminada y su tramitación notarial.',
	ONC: 'Envío solicitudes para obra nueva en construcción y su tramitación notarial.',
	LEGITIMACIONES:
		'Envía solicitudes de legitimación de firmas y gestiona su tramitación notarial de forma digital.',
};

const ItemCarouselComponent = ({
	hoverContent,
	icon,
	title,
}: {
	readonly hoverContent: string;
	readonly icon: Icon;
	readonly title: string;
}) => (
	<CTNCard
		aspectRatio={false}
		className="c-pnp-card"
		overlay={<CTNCardOverlayTemplate className="c-ctn-card-icon c-ctn-card-icon--back" content={hoverContent} />}
	>
		<CardIconTemplate icon={icon} iconColor="blue-50" title={title} />
	</CTNCard>
);

const gallery: Array<{ id: string; hoverContent: string; icon: Icon; title: string }> = [
	{
		id: '1',
		hoverContent: CONTENT.DON,
		icon: ROUTES_ICON.DON,
		title: ROUTES_LABEL.DON,
	},
	{
		id: '2',
		hoverContent: CONTENT.ONC,
		icon: ROUTES_ICON.ONC,
		title: ROUTES_LABEL.ONC,
	},
	{
		id: '3',
		hoverContent: CONTENT.DIVISION_HORIZONTAL,
		icon: ROUTES_ICON.DIVISION_HORIZONTAL,
		title: ROUTES_LABEL.DIVISION_HORIZONTAL,
	},
	{
		id: '4',
		hoverContent: CONTENT.ACTA_FIN_OBRA,
		icon: ROUTES_ICON.ACTA_FIN_OBRA,
		title: ROUTES_LABEL.ACTA_FIN_OBRA,
	},
	{
		id: '5',
		hoverContent: CONTENT.PODERES,
		icon: ROUTES_ICON.PODERES,
		title: ROUTES_LABEL.PODERES,
	},
	{
		id: '6',
		hoverContent: CONTENT.COMPRAVENTA,
		icon: ROUTES_ICON.COMPRAVENTA,
		title: ROUTES_LABEL.COMPRAVENTA,
	},
	{
		id: '7',
		hoverContent: CONTENT.LEGITIMACIONES,
		icon: ROUTES_ICON.LEGITIMACIONES,
		title: ROUTES_LABEL.LEGITIMACIONES,
	},
];

const galerySettings: Settings = {
	dots: true,
	swipe: true,
	infinite: true,
	arrows: true,
	responsive: [
		{
			breakpoint: 6000,
			settings: {
				slidesToShow: 4,
				slidesToScroll: 4,
			},
		},
		{
			breakpoint: 1023,
			settings: {
				slidesToShow: 3,
				slidesToScroll: 3,
			},
		},
		{
			breakpoint: 767,
			settings: {
				slidesToShow: 1,
				slidesToScroll: 1,
			},
		},
	],
};

const Landing = () => {
	const location = useLocation() as unknown as Location<{ data: { message: string; errores: string[] } }>;
	const { data } = location?.state || {};
	const { openNotification, closeNotification } = useNotification();
	const notificationTemplate = notificationsConfig({ closeNotification });

	const llamadaKeycloak = async () => {
		const { code, data } = await getKeyCloakLogin();
		if (code === 200) {
			window.location.replace(data);
		}
	};


	useEffect(() => {
		if (data) {
			const { message, errores } = data;
			openNotification(notificationTemplate.error406({ message, errores, onClickAccept() { 
				void llamadaKeycloak(); 
			} }));
		}
	}, [data]);

	return (
		<div className="p-landing">
			<main>
				<div className="p-landing__section p-landing__section--hero">
					<header className="p-landing__header">
						<h1 className="p-landing__brand">
							<ImageMedia
								alt="Sede Electrónica Notarial. Empresa y personas"
								src={`${ROUTES_RESOURCES.IMAGES}/logo-home.svg`}
								mediaSources={[
									{
										media: `(width >= ${BREAKPOINTS.PORTRAIT}px)`,
										srcSet: `${ROUTES_RESOURCES.IMAGES}/logo-homex2.svg`,
									},
								]}
							/>
						</h1>
						<CTNButton
							className="p-landing__btn"
							label="Acceder"
							size="size-40"
							onClick={llamadaKeycloak}
						/>
					</header>
					<div className="p-landing__subheader">
						<h2 className="p-landing__tit">Gestiona las solicitudes notariales de tu empresa</h2>
						<p className="[ p-landing__txt ] [ u-mt-24 ]">
							Accede desde cualquier dispositivo autorizado a toda la información notarial de tu empresa.
						</p>
					</div>
					<img className="p-landing__img" src={Dispositivos} alt="" />
					<figure className="p-landing__figure">
						<img className="p-landing__bg" src={BackgroundImage} alt="" />
					</figure>
				</div>
				<ul className="p-landing__section p-landing__section--items">
					<li>
						<div className="p-landing__item">
							<CTNIcon icon="documentOk" color="blue-50" size="size-64" />
							<h3 className="p-landing__item-tit">Envía solicitudes de una forma sencilla</h3>
							<p className="p-landing__item-info">
								Accede a los actos notariales y crea nuevas solicitudes en solo tres pasos.
							</p>
						</div>
					</li>
					<li>
						<div className="p-landing__item">
							<CTNIcon icon="watchOutline" color="blue-50" size="size-64" />
							<h3 className="p-landing__item-tit">Recibe notificaciones al instante</h3>
							<p className="p-landing__item-info">Recibe avisos por correo electrónico cuando haya actualizaciones en el portal.</p>
						</div>
					</li>
					<li>
						<div className="p-landing__item">
							<CTNIcon icon="check" color="blue-50" size="size-64" />
							<h3 className="p-landing__item-tit">Consulta el estado de tus gestiones</h3>
							<p className="p-landing__item-info">
								Sigue el progreso de tus solicitudes con información actualizada, trazabilidad e informes, y gestiona la documentación fácilmente.
							</p>
						</div>
					</li>
				</ul>
				<section className="p-landing__section p-landing__section--cards">
					<header className="p-landing__subheader">
						<h2 className="p-landing__tit">¿Qué puedes hacer en el portal?</h2>
						<p className="[ p-landing__txt ] [ u-mt-24 ]">
							El Portal Notarial Multisectorial te permite acceder a múltiples servicios y trámites notariales, 
							como División horizontal, Compraventa, Poderes, Legitimación
							de firmas, Acta final de obra o Declaración de obra nueva, entre otros.
						</p>
					</header>
					<div className="[ p-landing__cards ] [ u-mt-40 ]">
						<CTNCarousel iconButtonNext="arrowRight" iconButtonPrev="arrowLeft" settings={galerySettings}>
							{gallery.map(({ id, icon, hoverContent, title }) => (
								<ItemCarouselComponent key={id} hoverContent={hoverContent} icon={icon} title={title} />
							))}
						</CTNCarousel>
					</div>
				</section>
			</main>
			<Footer className="p-landing__footer" origin='public'/>
		</div>
	);
};

export default Landing;
